import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Button, Stack, Avatar, 
  Breadcrumbs, Link, Divider, Card, CardContent, CardActionArea,
  Skeleton, Chip
} from '@mui/material';
import { 
  School, LibraryBooks, Assessment, EventNote, 
  Group, TrendingUp, ChevronRight, Settings, AddCircleOutline, Star
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const AcademicsDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const userInfo = localStorage.getItem('userInfo');
        const token = userInfo ? JSON.parse(userInfo).token : null;
        const res = await axios.get(getApiUrl('academics/stats'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(res.data);
      } catch (err) {
        console.error('Error fetching academic stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const modules = [
    { title: 'Subjects', icon: <LibraryBooks />, path: '/dashboard/subjects', color: '#4C51BF', count: stats?.subjectCount || 0, desc: 'Manage curriculum & credits' },
    { title: 'Exams', icon: <Assessment />, path: '/dashboard/exams', color: '#3182CE', count: stats?.examCount || 0, desc: 'Schedule & mark assessments' },
    { title: 'Grades & Reports', icon: <Star />, path: '/dashboard/grades', color: '#F9B43F', count: '98% Entry', desc: 'Results & performance' },
    { title: 'Attendance', icon: <TrendingUp />, path: '/dashboard/attendance', color: '#38A169', count: `${stats?.attendancePercentage || 0}%`, desc: 'Track daily presence' },
    { title: 'Timetable', icon: <EventNote />, path: '/dashboard/timetable', color: '#805AD5', count: 'Active', desc: 'Class & teacher schedules' },
    { title: 'Class Groups', icon: <Group />, path: '/dashboard/groups', color: '#D69E2E', count: stats?.groupCount || 0, desc: 'Student categorization' },
  ];

  return (
    <Box sx={{ p: 4, minHeight: '100vh', bgcolor: '#F7FAFC' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 1, fontSize: '0.85rem' }}>
          <Link underline="hover" color="inherit" onClick={() => navigate('/dashboard')} sx={{ cursor: 'pointer' }}>Dashboard</Link>
          <Typography color="text.primary">Academics Hub</Typography>
        </Breadcrumbs>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h3" fontWeight={800} sx={{ color: '#263B7C', letterSpacing: '-0.02em', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
              Academics Hub
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Central control for your school's curriculum, assessments, and tracking.
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            startIcon={<Settings />}
            onClick={() => navigate('/dashboard/groups')}
            sx={{ 
                bgcolor: '#263B7C', borderRadius: '12px', px: 3, py: 1.5,
                boxShadow: '0 4px 14px rgba(38,59,124,0.25)',
                '&:hover': { bgcolor: '#1e2e60' }
            }}
          >
            Quick Config
          </Button>
        </Stack>
      </Box>

      {/* Stats Ribbon - using CSS Grid */}
      <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, 
          gap: 3, 
          mb: 5 
      }}>
        {[
          { label: 'Total Subjects', value: stats?.subjectCount, icon: <LibraryBooks />, color: '#4C51BF' },
          { label: 'Active Exams', value: stats?.examCount, icon: <Assessment />, color: '#38B2AC' },
          { label: 'Class Groups', value: stats?.groupCount, icon: <School />, color: '#ED8936' },
          { label: 'Avg Attendance', value: `${stats?.attendancePercentage}%`, icon: <TrendingUp />, color: '#48BB78' }
        ].map((stat, i) => (
          <Paper key={i} sx={{ 
            p: 3, borderRadius: '20px', display: 'flex', alignItems: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #edf2f7'
          }}>
            <Avatar sx={{ bgcolor: `${stat.color}15`, color: stat.color, mr: 2, width: 56, height: 56 }}>
              {stat.icon}
            </Avatar>
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                {stat.label}
              </Typography>
              <Typography variant="h4" fontWeight={800} color="#2D3748">
                {loading ? <Skeleton width={40} /> : stat.value}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Main Content Areas */}
      <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', lg: '3fr 1fr' }, 
          gap: 4 
      }}>
        {/* Modules Section */}
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ mb: 3, color: '#2D3748' }}>
            Academic Modules
          </Typography>
          <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, 
              gap: 3 
          }}>
            {modules.map((module, i) => (
              <Card key={i} sx={{ 
                  borderRadius: '24px', transition: 'all 0.3s', border: '1px solid #edf2f7',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                  '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(0,0,0,0.08)' }
              }}>
                <CardActionArea onClick={() => navigate(module.path)} sx={{ p: 1 }}>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Avatar sx={{ 
                        bgcolor: `${module.color}15`, color: module.color, 
                        width: 80, height: 80, mx: 'auto', mb: 3,
                        fontSize: '2.5rem'
                    }}>
                      {module.icon}
                    </Avatar>
                    <Typography variant="h6" fontWeight={800} color="#2D3748" gutterBottom>
                      {module.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 2 }}>
                      {module.desc}
                    </Typography>
                    <Chip 
                      label={module.count} 
                      size="small" 
                      sx={{ fontWeight: 800, bgcolor: module.color, color: 'white' }} 
                    />
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Sidebar */}
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ mb: 3, color: '#2D3748' }}>
            Academic Calendar
          </Typography>
          <Paper sx={{ p: 3, borderRadius: '24px', border: '1px solid #edf2f7', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
             <Stack spacing={3}>
                {[
                    { title: 'Final Exams', date: 'Dec 12 - 20', type: 'Exam' },
                    { title: 'Term Break', date: 'Dec 21 - Jan 5', type: 'Holiday' },
                    { title: 'Result Day', date: 'Jan 10', type: 'Event' }
                ].map((item, i) => (
                    <Box key={i}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Typography variant="body2" fontWeight={800} color="#2D3748">{item.title}</Typography>
                            <Chip label={item.type} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <EventNote sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">{item.date}</Typography>
                        </Stack>
                        {i < 2 && <Divider sx={{ mt: 2 }} />}
                    </Box>
                ))}
                
                <Button 
                    fullWidth 
                    variant="outlined" 
                    endIcon={<ChevronRight />}
                    onClick={() => navigate('/dashboard/timetable')}
                    sx={{ borderRadius: '12px', py: 1.2, fontWeight: 700, mt: 1 }}
                >
                    View Full Schedule
                </Button>
                
                <Box 
                    onClick={() => navigate('/dashboard/exams')}
                    sx={{ bgcolor: '#4C51BF10', p: 2, borderRadius: '16px', mt: 1, cursor: 'pointer', transition: 'all 0.2s', '&:hover': { bgcolor: '#4C51BF20' } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ bgcolor: '#4C51BF', width: 32, height: 32 }}>
                            <AddCircleOutline sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Box>
                            <Typography variant="caption" fontWeight={800} color="#4C51BF" display="block">QUICK ACTION</Typography>
                            <Typography variant="body2" fontWeight={700}>Add New Assessment</Typography>
                        </Box>
                    </Stack>
                </Box>
             </Stack>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default AcademicsDashboardPage;
